import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Bold, BookOpen, Check, CircleAlert, CircleCheck, Cloud, Code, Columns2, ExternalLink, Eye, FileQuestion, FileText, Globe,
  Hash, Heading2, Hourglass, Image as ImageIcon, ImagePlus, Italic, Layers, Link2, List, Lock, PenLine, Plus, Quote, RefreshCw, Send,
  ShieldAlert, ShieldCheck, Sparkles, Users,
  Tags, Trash2, TriangleAlert, X,
} from 'lucide-react';
import { apiErrorMessage, articleReviewService, articleService, lessonService, tagService, topicService } from '../services/api';
import { useCourse } from '../contexts/CourseContext';
import {
  Button, Card, EmptyState, Field, GlobalShell, Loader, Modal, SectionTitle,
} from '../components/ui';
import { ArticleBody } from '../components/articles/ArticleBody';
import { LinkPreviewThumb, linkedInInspectorUrl } from '../components/articles/LinkPreview';
import { ReviewPanel } from '../components/review/ReviewPanel';
import { REVIEW_LOOKS_FOR } from '../components/review/findings';
import { articleUrl, readingMinutes, relativeTime, wordCount } from '../utils/articles';
import './Articles.css';
import './ArticleEditor.css';

const AUTOSAVE_MS = 1200;

const VISIBILITY = [
  { value: 'members', icon: Users, label: 'Só alunos', text: 'Publica na hora' },
  { value: 'public', icon: Globe, label: 'Público', text: 'Passa pela revisão da IA' },
];

const LINK_KIND = {
  topic: { icon: BookOpen, label: (l) => `Tópico · Fase ${l.fase}` },
  lesson: { icon: FileText, label: (l) => `Aula ${l.lessonNumber ?? ''} · ${l.topicName}` },
  question_set: { icon: FileQuestion, label: () => 'Questões do tópico' },
  flashcard_set: { icon: Layers, label: () => 'Flashcards do tópico' },
};

/** Editor de artigo: novo (/artigos/novo) e edição (/artigos/:id/editar), com salvamento automático. */
function ArticleEditor() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { courses } = useCourse();

  const [id, setId] = useState(routeId ? Number(routeId) : null);
  const [loading, setLoading] = useState(Boolean(routeId));
  const [loadError, setLoadError] = useState(false);
  const [article, setArticle] = useState(() => ({
    title: '',
    subtitle: '',
    previewText: '',
    bodyMd: '',
    coverUrl: null,
    visibility: 'members',
    tags: location.state?.tags || [],
    courseIds: [],
    // "Escrever sobre este tópico" já chega com o vínculo
    links: location.state?.links || [],
  }));
  const [status, setStatus] = useState('draft');
  const [slug, setSlug] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('split');
  const [busy, setBusy] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [linking, setLinking] = useState(false);
  // Revisão por IA: conteúdo aprovado para público, última revisão, painel 3b
  const [approved, setApproved] = useState(false);
  const [contentHash, setContentHash] = useState(null);
  const [wasPublic, setWasPublic] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(null);
  const [, setTick] = useState(0);
  const textRef = useRef(null);
  const saveTimer = useRef(null);
  const creating = useRef(null);
  // Refs: o salvamento automático sempre usa o id e o conteúdo mais recentes
  const idRef = useRef(id);
  const articleRef = useRef(article);

  useEffect(() => {
    if (!routeId) return;
    articleService.forEdit(routeId)
      .then(a => {
        const loaded = {
          title: a.title || '',
          subtitle: a.subtitle || '',
          previewText: a.previewText || '',
          bodyMd: a.bodyMd || '',
          coverUrl: a.coverUrl,
          visibility: a.visibility,
          tags: a.tags,
          courseIds: a.courseIds,
          links: a.links,
        };
        articleRef.current = loaded;
        setArticle(loaded);
        setStatus(a.status);
        setSlug(a.slug);
        setSavedAt(a.updatedAt);
        setApproved(!!a.approved);
        setContentHash(a.contentHash);
        setWasPublic(!!a.wasPublic);
        articleReviewService.current(a.id).then(r => setReview(r.review)).catch(() => {});
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [routeId]);

  // "Salvo há 1 min" se atualiza sozinho
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const payload = useCallback((a) => ({
    title: a.title,
    subtitle: a.subtitle,
    previewText: a.previewText,
    bodyMd: a.bodyMd,
    coverUrl: a.coverUrl,
    visibility: a.visibility,
    tags: a.tags,
    courseIds: a.courseIds,
    links: a.links.map(l => ({ kind: l.kind, targetId: l.targetId })),
  }), []);

  /** Grava agora. O primeiro salvamento cria o rascunho. */
  const saveNow = useCallback(async (a) => {
    clearTimeout(saveTimer.current);
    setSaving(true);
    setError(null);
    try {
      // Se o rascunho ainda está sendo criado, espera e depois atualiza (não cria dois)
      if (!idRef.current && creating.current) await creating.current.catch(() => {});
      let saved;
      if (idRef.current) {
        saved = await articleService.update(idRef.current, payload(a));
      } else {
        creating.current = articleService.create(payload(a));
        saved = await creating.current;
        idRef.current = saved.id;
        setId(saved.id);
        window.history.replaceState(null, '', `/artigos/${saved.id}/editar`);
      }
      setSlug(saved.slug);
      setStatus(saved.status);
      setSavedAt(saved.updatedAt);
      setApproved(!!saved.approved);
      setContentHash(saved.contentHash);
      setDirty(false);
      return saved;
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível salvar. Suas alterações ficam aqui até conseguir.'));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [payload]);

  const update = (patch) => {
    const next = { ...articleRef.current, ...patch };
    articleRef.current = next;
    setArticle(next);
    setDirty(true);
    clearTimeout(saveTimer.current);
    if (next.title.trim() || next.bodyMd.trim()) {
      saveTimer.current = setTimeout(() => saveNow(articleRef.current).catch(() => {}), AUTOSAVE_MS);
    }
  };

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const publish = async () => {
    const missing = !article.title.trim() ? 'Dê um título ao artigo.'
      : !article.bodyMd.trim() ? 'Escreva o texto do artigo.'
        : article.tags.length === 0 ? 'Escolha pelo menos um tema.' : null;
    if (missing) {
      setError(missing);
      return;
    }
    // Público só depois de passar pela revisão
    if (articleRef.current.visibility === 'public' && !approved) {
      runReview(true);
      return;
    }
    setBusy(true);
    try {
      const saved = await saveNow(articleRef.current);
      const published = await articleService.publish(saved.id);
      navigate(`/artigos/${published.slug}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível publicar.'));
    } finally {
      setBusy(false);
    }
  };

  /** "Revisar com IA" (só informa) ou "Revisar e publicar" como público. */
  const runReview = async (publishPublic) => {
    if (publishPublic) {
      const a = articleRef.current;
      const missing = !a.title.trim() ? 'Dê um título ao artigo.'
        : !a.bodyMd.trim() ? 'Escreva o texto do artigo.'
          : a.tags.length === 0 ? 'Escolha pelo menos um tema.' : null;
      if (missing) {
        setError(missing);
        return;
      }
    }
    setReviewBusy(publishPublic ? 'publish' : 'check');
    setError(null);
    try {
      const saved = await saveNow(articleRef.current);
      const r = await articleReviewService.review(saved.id, publishPublic);
      setReview(r.review);
      setApproved(!!r.approved);
      setContentHash(r.contentHash);
      setStatus(r.status);
      if (r.published) {
        navigate(`/artigos/${saved.slug}`);
        return;
      }
      if (publishPublic || (r.review && r.review.findings.length > 0 && reviewOpen)) setReviewOpen(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para revisar agora. Tente de novo em instantes.'));
    } finally {
      setReviewBusy(null);
    }
  };

  /** Abre o painel; pontos cujo trecho já sumiu do texto contam como corrigidos. */
  const openReview = async () => {
    setReviewOpen(true);
    if (!review || review.status !== 'FINDINGS') return;
    const a = articleRef.current;
    const where = (f) => (f.field === 'title' ? a.title : f.field === 'subtitle' ? a.subtitle : a.bodyMd) || '';
    for (const f of review.findings.filter(x => x.state === 'OPEN' && !where(x).includes(x.excerpt))) {
      // eslint-disable-next-line no-await-in-loop
      await setFindingState(f, 'FIXED');
    }
  };

  const setFindingState = async (f, state, justification) => {
    try {
      const r = await articleReviewService.updateFinding(idRef.current, review.id, f.id, state, justification);
      setReview(r.review);
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para atualizar o ponto.'));
    }
  };

  const useSuggestion = async (f) => {
    const a = articleRef.current;
    const field = f.field === 'title' ? 'title' : f.field === 'subtitle' ? 'subtitle' : 'bodyMd';
    const value = a[field] || '';
    let start = Number.isInteger(f.start) && value.slice(f.start, f.end) === f.excerpt ? f.start : value.indexOf(f.excerpt);
    if (start < 0) start = value.toLowerCase().indexOf(f.excerpt.toLowerCase());
    if (start >= 0) {
      update({ [field]: value.slice(0, start) + (f.suggestion || '') + value.slice(start + f.excerpt.length) });
    }
    await setFindingState(f, 'FIXED');
  };

  const editExcerpt = (f) => {
    setReviewOpen(false);
    if (f.field && f.field !== 'body') return;
    setMode(m => (m === 'preview' ? 'split' : m));
    requestAnimationFrame(() => {
      const el = textRef.current;
      if (!el) return;
      const i = el.value.indexOf(f.excerpt);
      el.focus();
      if (i >= 0) {
        el.setSelectionRange(i, i + f.excerpt.length);
        const line = el.value.slice(0, i).split('\n').length;
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY + line * 24 - 240, behavior: 'smooth' });
      }
    });
  };

  const publishMembers = async () => {
    setReviewOpen(false);
    const next = { ...articleRef.current, visibility: 'members' };
    articleRef.current = next;
    setArticle(next);
    setBusy(true);
    try {
      const saved = await saveNow(next);
      const published = await articleService.publish(saved.id);
      navigate(`/artigos/${published.slug}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível publicar.'));
    } finally {
      setBusy(false);
    }
  };

  const setStatusTo = async (next) => {
    if (next === status) return;
    if (next === 'published') {
      publish();
      return;
    }
    setBusy(true);
    try {
      if (dirty) await saveNow(articleRef.current);
      const r = await articleService.unpublish(idRef.current);
      setStatus(r.status);
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível voltar para rascunho.'));
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    clearTimeout(saveTimer.current);
    setBusy(true);
    try {
      if (idRef.current) await articleService.remove(idRef.current);
      navigate('/artigos?aba=mine', { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível descartar.'));
      setBusy(false);
      setConfirmDiscard(false);
    }
  };

  // ── Markdown: insere a sintaxe em volta da seleção ──────────────────
  const wrap = (before, after = '', placeholder = '') => {
    const el = textRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e, value } = el;
    const selected = value.slice(s, e) || placeholder;
    const next = value.slice(0, s) + before + selected + after + value.slice(e);
    update({ bodyMd: next });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + before.length, s + before.length + selected.length);
    });
  };
  const linePrefix = (prefix) => {
    const el = textRef.current;
    if (!el) return;
    const { selectionStart: s, value } = el;
    const lineStart = value.lastIndexOf('\n', s - 1) + 1;
    update({ bodyMd: value.slice(0, lineStart) + prefix + value.slice(lineStart) });
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + prefix.length, s + prefix.length); });
  };
  const insertImage = async (file) => {
    if (!file) return;
    try {
      const url = await articleService.uploadCover(file);
      wrap(`![${file.name.replace(/\.[^.]+$/, '')}](${url})`, '', '');
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível enviar a imagem.'));
    }
  };

  const TOOLS = [
    { icon: Heading2, label: 'Título de seção', run: () => linePrefix('## ') },
    { icon: Bold, label: 'Negrito', run: () => wrap('**', '**', 'texto') },
    { icon: Italic, label: 'Itálico', run: () => wrap('_', '_', 'texto') },
    { icon: Link2, label: 'Link', run: () => wrap('[', '](https://)', 'texto do link') },
    { icon: List, label: 'Lista', run: () => linePrefix('- ') },
    { icon: Quote, label: 'Citação', run: () => linePrefix('> ') },
    { icon: Code, label: 'Bloco de código', run: () => wrap('\n```\n', '\n```\n', 'código') },
  ];

  const words = wordCount(article.bodyMd);

  // A área de escrita cresce com o texto: quem rola é a página, e a toolbar fica presa no topo
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [article.bodyMd, mode, loading]);

  if (loadError) {
    return (
      <GlobalShell>
        <EmptyState
          file="editor.xml"
          title="Artigo indisponível"
          description="Ele não existe ou você não é o autor."
          action={<Button onClick={() => navigate('/artigos')}>Ver artigos</Button>}
        />
      </GlobalShell>
    );
  }
  if (loading) return <GlobalShell><Loader label="Abrindo o editor..." /></GlobalShell>;

  const published = status === 'published';
  const publicIntent = article.visibility === 'public';
  const reviewPending = review?.status === 'PENDING' && review?.contentHash === contentHash;
  // Publicado, quer público, mas o texto mudou desde a aprovação: está "Só alunos" até revisar de novo
  const needsReview = published && publicIntent && !approved;
  const reviewCurrent = !!review && !dirty && review.contentHash === contentHash;
  const openArticle = async () => {
    if (dirty) await saveNow(articleRef.current).catch(() => {});
    navigate(`/artigos/${slug}`);
  };

  return (
    <GlobalShell>
      <div className="ated-bar">
        <div className="ated-bar__inner">
          <button type="button" className="ated-bar__back" onClick={() => navigate('/artigos')}>
            <ArrowLeft size={15} aria-hidden="true" /><span>Artigos</span>
          </button>
          <span className="ated-bar__sep" aria-hidden="true" />
          <h1 className="ated-bar__title">{id ? 'Editar artigo' : 'Novo artigo'}</h1>
          <span className={`ated-bar__status ${published ? 'is-published' : ''}`}>
            {published ? <Globe size={11} aria-hidden="true" /> : <PenLine size={11} aria-hidden="true" />}
            {published ? 'Publicado' : 'Rascunho'}
          </span>
          <span className="ated-bar__saved" aria-live="polite">
            {saving ? 'Salvando...' : savedAt ? (
              <><Cloud size={13} aria-hidden="true" className={dirty ? '' : 'is-ok'} />{dirty ? 'Alterações não salvas' : `Salvo ${relativeTime(savedAt)}`}</>
            ) : 'Salva sozinho'}
          </span>
          <span className="ated-bar__spacer" />
          <button type="button" className="ated-bar__review" onClick={() => runReview(false)} disabled={!!reviewBusy || busy}
                  title="Procura clientes, dados internos, credenciais e pessoas no texto. Não bloqueia.">
            <ShieldCheck size={14} aria-hidden="true" />{reviewBusy === 'check' ? 'Revisando...' : 'Revisar com IA'}
          </button>
          {published && !needsReview ? (
            <button type="button" className="ated-bar__cta" onClick={openArticle}>
              <ExternalLink size={14} aria-hidden="true" />Ver artigo
            </button>
          ) : (
            <button type="button" className="ated-bar__cta" onClick={publish} disabled={busy || !!reviewBusy || reviewPending}>
              {reviewPending ? <Hourglass size={14} aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
              {reviewPending ? 'Aguardando admin' : reviewBusy === 'publish' ? 'Revisando...' : publicIntent && !approved ? 'Revisar e publicar' : 'Publicar'}
            </button>
          )}
        </div>
      </div>

      <ReviewChip review={review} current={reviewCurrent} onOpen={openReview} />

      {error && (
        <div className="ated-error" role="alert">
          <CircleAlert size={18} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <div className="ated-grid">
        <Card className="ated-main" padding="none">
          <div className="ated-head">
            <input
              className="ated-title"
              value={article.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Título do artigo"
              aria-label="Título"
              maxLength={200}
            />
            <input
              className="ated-subtitle"
              value={article.subtitle}
              onChange={(e) => update({ subtitle: e.target.value })}
              placeholder="Subtítulo: uma frase sobre o que a pessoa vai aprender"
              aria-label="Subtítulo"
              maxLength={300}
            />
          </div>

          <div className="ated-toolbar" data-tour="ed-toolbar">
            <div className="ated-tools" role="toolbar" aria-label="Formatação">
              {TOOLS.map(t => (
                <button key={t.label} type="button" onClick={t.run} title={t.label} aria-label={t.label} disabled={mode === 'preview'}>
                  <t.icon size={16} aria-hidden="true" />
                </button>
              ))}
              <label className={`ated-tools__file ${mode === 'preview' ? 'is-disabled' : ''}`} title="Imagem" aria-label="Inserir imagem">
                <ImageIcon size={16} aria-hidden="true" />
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden disabled={mode === 'preview'}
                       onChange={(e) => { insertImage(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
            </div>
            <div className="ated-modes" role="group" aria-label="Modo de edição">
              {[['text', FileText, 'Texto'], ['split', Columns2, 'Dividido'], ['preview', Eye, 'Prévia']].map(([v, Icon, label]) => (
                <button key={v} type="button" className={mode === v ? 'is-active' : ''} aria-pressed={mode === v} onClick={() => setMode(v)}>
                  <Icon size={13} aria-hidden="true" /> {label}
                </button>
              ))}
            </div>
          </div>

          <div className={`ated-panes ated-panes--${mode}`}>
            {mode !== 'preview' && (
              <textarea
                ref={textRef}
                className="ated-text"
                value={article.bodyMd}
                onChange={(e) => update({ bodyMd: e.target.value })}
                placeholder={'## Primeira seção\n\nEscreva em Markdown. Use ## para as seções: elas viram o sumário do artigo.'}
                aria-label="Texto do artigo em Markdown"
                spellCheck
              />
            )}
            {mode !== 'text' && (
              <div className="ated-preview">
                {article.bodyMd.trim()
                  ? <ArticleBody markdown={article.bodyMd} className="art-body--preview" />
                  : <p className="ated-preview__empty">A prévia aparece aqui.</p>}
              </div>
            )}
          </div>
          <div className="ated-count">
            <span>Markdown</span>
            <span>{words.toLocaleString('pt-BR')} {words === 1 ? 'palavra' : 'palavras'}</span>
            <span>~{readingMinutes(words)} min de leitura</span>
          </div>
        </Card>

        <Card as="aside" className="ated-panel" padding="none" aria-label="Configurações do artigo">
          <section className="ated-sec" data-tour="ed-publicacao">
            <SectionTitle icon={Send}>Publicação</SectionTitle>
            <div className="ated-status" role="group" aria-label="Status">
              {[['draft', PenLine, 'Rascunho'], ['published', Globe, 'Publicado']].map(([v, Icon, label]) => (
                <button key={v} type="button" className={status === v ? 'is-active' : ''} aria-pressed={status === v}
                        onClick={() => setStatusTo(v)} disabled={busy || (v === 'draft' && !id)}>
                  <Icon size={13} aria-hidden="true" />{label}
                </button>
              ))}
            </div>
            {!published && <span className="ated-hint">Rascunhos só aparecem para você, em Meus artigos.</span>}
            <div className="ated-field">
              <span className="ated-label">Quem pode ler <span className="ated-req">*</span></span>
              <div className="ated-visibility" role="radiogroup" aria-label="Quem pode ler">
                {VISIBILITY.map(v => (
                  <label key={v.value} className={`ated-radio ${article.visibility === v.value ? 'is-active' : ''}`}>
                    <input
                      type="radio"
                      name="visibility"
                      value={v.value}
                      checked={article.visibility === v.value}
                      onChange={() => update({ visibility: v.value })}
                    />
                    <span>
                      <strong><v.icon size={13} aria-hidden="true" /> {v.label}</strong>
                      <span>{v.value === 'public' && approved && !dirty ? 'Texto já revisado' : v.text}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            {wasPublic && !publicIntent && (
              <p className="ated-reviewnote ated-reviewnote--info">
                <ShieldAlert size={14} aria-hidden="true" />
                <span>O artigo era público e voltou para "Só alunos" porque o texto mudou. Escolha "Público" e revise para republicar.</span>
              </p>
            )}
            {review?.status === 'REJECTED' && (
              <p className="ated-reviewnote ated-reviewnote--bad">
                <ShieldAlert size={14} aria-hidden="true" />
                <span><strong>Um admin pediu ajuste:</strong> {review.reviewerComment}</span>
              </p>
            )}
            {publicIntent && (!approved || dirty) && (
              <div className="ated-review">
                <span className="ated-review__title">A revisão procura</span>
                <ul>
                  {REVIEW_LOOKS_FOR.map(r => <li key={r.label}><r.icon size={13} aria-hidden="true" />{r.label}</li>)}
                </ul>
                {needsReview && (
                  <p className="ated-reviewnote ated-reviewnote--info">
                    <ShieldAlert size={14} aria-hidden="true" />
                    <span>O texto mudou desde a aprovação: o artigo está como "Só alunos" até passar de novo.</span>
                  </p>
                )}
                {reviewPending ? (
                  <Button block variant="outline" icon={Hourglass} onClick={openReview}>Aguardando um admin</Button>
                ) : (
                  <Button block icon={ShieldCheck} className="ated-review__btn" onClick={() => runReview(true)}
                          disabled={!!reviewBusy || busy}>
                    {reviewBusy === 'publish' ? 'Revisando...' : 'Revisar e publicar'}
                  </Button>
                )}
                <span className="ated-hint ated-review__hint">Leva uns 20 segundos. {published ? 'Enquanto isso, só alunos leem.' : 'O artigo fica como rascunho até passar.'}</span>
              </div>
            )}
          </section>

          <LinkPreviewSection article={article} slug={slug} published={published} onChange={(previewText) => update({ previewText })} />

          <section className="ated-sec" data-tour="ed-organizacao">
            <SectionTitle icon={Tags}>Organização</SectionTitle>
            <div className="ated-field">
              <TagInput tags={article.tags} onChange={(tags) => update({ tags })} />
              <Appearances tags={article.tags} links={article.links} />
            </div>
            <div className="ated-field">
              <span className="ated-label">Cursos relacionados <span className="ated-optional">· opcional</span></span>
              <div className="ated-courses">
                {courses.map(c => {
                  const on = article.courseIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`ated-chip ${on ? 'is-active' : ''}`}
                      aria-pressed={on}
                      onClick={() => update({ courseIds: on ? article.courseIds.filter(x => x !== c.id) : [...article.courseIds, c.id] })}
                    >
                      {on ? <Check size={11} aria-hidden="true" /> : <Plus size={11} aria-hidden="true" />}{c.name}
                    </button>
                  );
                })}
              </div>
              {article.courseIds.length === 0 && (
                <span className="ated-hint">Sem curso, o artigo aparece em Artigos e na página de cada tema.</span>
              )}
            </div>
          </section>

          <section className="ated-sec">
            <SectionTitle icon={Sparkles}>Extras</SectionTitle>
            <CoverField coverUrl={article.coverUrl} onChange={(coverUrl) => update({ coverUrl })} onError={setError} />
            {article.links.length > 0 && (
              <div className="ated-links">
                {article.links.map((l, i) => {
                  const kind = LINK_KIND[l.kind];
                  return (
                    <div key={`${l.kind}-${l.targetId}`} className="ated-link">
                      <span className="ated-link__icon" style={l.kind === 'topic' ? { background: l.courseColor || 'var(--navy)', color: '#fff' } : undefined}>
                        <kind.icon size={14} aria-hidden="true" />
                      </span>
                      <span className="ated-link__text">
                        <span>{kind.label(l)}</span>
                        <strong>{l.kind === 'topic' || l.kind === 'lesson' ? l.name : l.topicName}</strong>
                      </span>
                      <button type="button" className="ss-iconbtn" aria-label="Remover vínculo"
                              onClick={() => update({ links: article.links.filter((_, j) => j !== i) })}>
                        <X size={14} aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <button type="button" className="ated-linkbtn" onClick={() => setLinking(true)}>
              <Link2 size={13} aria-hidden="true" className="ated-linkbtn__icon" />
              <span>Vincular tópico, aula, questões…</span>
              <Plus size={13} aria-hidden="true" />
            </button>
          </section>

          <section className="ated-sec" data-tour="ed-perigo">
            <SectionTitle icon={TriangleAlert}>Zona de perigo</SectionTitle>
            <button type="button" className="ated-danger" onClick={() => setConfirmDiscard(true)} disabled={busy}>
              <Trash2 size={13} aria-hidden="true" />{published ? 'Excluir artigo' : 'Descartar rascunho'}
            </button>
          </section>
        </Card>
      </div>

      {reviewOpen && review && (
        <ReviewPanel
          article={article}
          review={review}
          busy={reviewBusy || (busy ? 'busy' : null)}
          onClose={() => setReviewOpen(false)}
          onUseSuggestion={useSuggestion}
          onEditExcerpt={editExcerpt}
          onDismiss={(f, text) => setFindingState(f, 'DISMISSED', text)}
          onReopen={(f) => setFindingState(f, 'OPEN')}
          onPublishPublic={() => runReview(true)}
          onPublishMembers={publishMembers}
        />
      )}

      {linking && (
        <LinkModal
          courses={courses}
          onClose={() => setLinking(false)}
          onAdd={(newLinks) => {
            const key = (l) => `${l.kind}:${l.targetId}`;
            const have = new Set(article.links.map(key));
            update({ links: [...article.links, ...newLinks.filter(l => !have.has(key(l)))] });
            setLinking(false);
          }}
        />
      )}

      <Modal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        title={published ? 'Excluir artigo' : 'Descartar rascunho'}
        width={460}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setConfirmDiscard(false)}>Cancelar</Button>
            <Button variant="danger" icon={Trash2} onClick={discard} disabled={busy}>{published ? 'Excluir' : 'Descartar'}</Button>
          </>
        )}
      >
        <p>{published
          ? 'O artigo sai do ar, com as curtidas e os comentários. Não dá para desfazer.'
          : 'O rascunho é apagado. Não dá para desfazer.'}</p>
      </Modal>
    </GlobalShell>
  );
}

/** Resultado do "Revisar com IA" logo abaixo da barra: nada encontrado ou N pontos para olhar. */
function ReviewChip({ review, current, onOpen }) {
  if (!review || !current) return null;
  const open = review.findings.filter(f => f.state === 'OPEN').length;
  if (review.status === 'PENDING') {
    return (
      <div className="ated-reviewchip ated-reviewchip--wait" role="status">
        <Hourglass size={16} aria-hidden="true" />
        <span><strong>Aguardando um admin</strong>Você justificou pontos da revisão; o aviso chega pelo sininho.</span>
        <button type="button" onClick={onOpen}>Ver</button>
      </div>
    );
  }
  if (review.findings.length === 0 || open === 0) {
    return (
      <div className="ated-reviewchip ated-reviewchip--ok" role="status">
        <CircleCheck size={16} aria-hidden="true" />
        <span><strong>{review.findings.length === 0 ? 'Nada encontrado' : 'Pontos resolvidos'}</strong>Revisado {relativeTime(review.createdAt)} · vale para publicar como público</span>
        {review.findings.length > 0 && <button type="button" onClick={onOpen}>Ver</button>}
      </div>
    );
  }
  return (
    <div className="ated-reviewchip ated-reviewchip--warn" role="status">
      <ShieldAlert size={16} aria-hidden="true" />
      <span><strong>{open} {open === 1 ? 'ponto' : 'pontos'} para olhar</strong>Só aviso: nada bloqueia enquanto é "Só alunos"</span>
      <button type="button" onClick={onOpen}>Ver</button>
    </div>
  );
}

const PREVIEW_MAX = 155;

/**
 * Prévia do link no LinkedIn e no Teams: miniatura, texto da prévia (o subtítulo, se vazio) e o botão
 * que manda o LinkedIn reler a página. "Só alunos" pede login, então a prévia mostra só a marca.
 */
function LinkPreviewSection({ article, slug, published, onChange }) {
  const isPublic = article.visibility === 'public';
  const text = article.previewText || '';
  const url = slug ? articleUrl({ slug, visibility: 'public' }) : null;
  return (
    <section className="ated-sec">
      <SectionTitle icon={ImageIcon}>Prévia do link</SectionTitle>
      <LinkPreviewThumb article={article} />
      <div className="ated-field">
        <label className="ated-label" htmlFor="ated-preview-text">
          Texto da prévia <span className="ated-optional">· usa o subtítulo se vazio · {text.length}/{PREVIEW_MAX}</span>
        </label>
        <textarea
          id="ated-preview-text"
          className="ated-previewtext"
          rows={3}
          maxLength={PREVIEW_MAX}
          value={text}
          placeholder={article.subtitle || 'Uma frase que convide a ler, como aparece no LinkedIn e no Teams.'}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="ated-hint">Imagem: a capa do artigo (1200×627) ou a capa gerada com título e tema.</span>
      </div>
      {isPublic ? (
        <div className="ated-previewfoot">
          <span className="ated-previewfoot__ok"><CircleCheck size={13} aria-hidden="true" />Robôs do LinkedIn e do Teams leem a página</span>
          <Button
            variant="outline"
            size="sm"
            icon={RefreshCw}
            disabled={!published || !url}
            title={published ? 'Abre o Post Inspector do LinkedIn, que renova a prévia guardada' : 'Disponível depois de publicar'}
            onClick={() => window.open(linkedInInspectorUrl(url), '_blank', 'noopener,noreferrer')}
          >
            Atualizar no LinkedIn
          </Button>
        </div>
      ) : (
        <p className="ated-previewnote">
          <Lock size={13} aria-hidden="true" />
          <span><strong>Só alunos:</strong> o link pede login, então o LinkedIn fica desabilitado e o Teams mostra só "Artigo para alunos do StudySpace" com a marca, sem o título nem o texto.</span>
        </p>
      )}
    </section>
  );
}

/** Temas com autocomplete; Enter ou vírgula adiciona, Backspace no campo vazio remove o último. */
function TagInput({ tags, onChange }) {
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const q = text.trim().replace(/^#/, '');
    if (!q) { setSuggestions([]); return undefined; }
    const t = setTimeout(() => {
      tagService.search(q).then(list => setSuggestions(list.filter(s => !tags.some(x => x.toLowerCase() === s.name.toLowerCase()))))
        .catch(() => setSuggestions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [text, tags]);

  const q = text.trim().replace(/^#/, '');
  const exact = suggestions.some(s => s.name.toLowerCase() === q.toLowerCase());
  const options = [
    ...suggestions.map(s => ({ name: s.name, count: s.articles })),
    ...(q && !exact && !tags.some(x => x.toLowerCase() === q.toLowerCase()) ? [{ name: q, create: true }] : []),
  ];

  const add = (name) => {
    const n = name.trim().replace(/^#/, '');
    if (n && !tags.some(x => x.toLowerCase() === n.toLowerCase())) onChange([...tags, n]);
    setText('');
    setActive(0);
  };

  const onKey = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && (options[active] || q)) {
      e.preventDefault();
      add(options[active]?.name || q);
    } else if (e.key === 'Backspace' && !text && tags.length) {
      onChange(tags.slice(0, -1));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(a + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    }
  };

  return (
    <div className="ated-tags">
      <span className="ated-label">Temas <span className="ated-req">*</span></span>
      <div className={`ated-taginput ${focused ? 'is-focused' : ''}`}>
        {tags.map(t => (
          <span key={t} className="ated-tag">
            {t}
            <button type="button" onClick={() => onChange(tags.filter(x => x !== t))} aria-label={`Remover ${t}`}><X size={12} aria-hidden="true" /></button>
          </span>
        ))}
        <input
          value={text}
          onChange={(e) => { setText(e.target.value); setActive(0); }}
          onKeyDown={onKey}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={tags.length ? '' : 'Ex.: Docker, Git, React'}
          aria-label="Adicionar tema"
        />
      </div>
      {focused && options.length > 0 && (
        <div className="ated-suggest" role="listbox">
          {options.map((o, i) => (
            <button
              key={o.create ? `new-${o.name}` : o.name}
              type="button"
              role="option"
              aria-selected={i === active}
              className={i === active ? 'is-active' : ''}
              onMouseDown={(e) => { e.preventDefault(); add(o.name); }}
            >
              {o.create ? <Plus size={14} aria-hidden="true" /> : <span className="ated-suggest__hash"><Hash size={14} aria-hidden="true" /></span>}
              {o.create ? `Criar tema “${o.name}”` : o.name}
              {!o.create && <span className="ated-suggest__count">{o.count} {o.count === 1 ? 'artigo' : 'artigos'}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Linha de apoio sob os temas: páginas dos temas e tópicos ligados aos temas ou vinculados. */
function Appearances({ tags, links }) {
  const [topics, setTopics] = useState([]);
  const key = tags.join(',');
  useEffect(() => {
    if (!key) { setTopics([]); return undefined; }
    const t = setTimeout(() => tagService.appearances(key.split(',')).then(r => setTopics(r.topics)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [key]);

  const linkedTopics = links.filter(l => l.kind === 'topic' || l.kind === 'lesson')
    .map(l => ({ topicId: l.topicId, name: l.topicName, fase: l.fase, courseName: l.courseName, direct: true }));
  const all = useMemo(() => {
    const seen = new Set();
    return [...linkedTopics, ...topics].filter(t => (seen.has(t.topicId) ? false : seen.add(t.topicId)));
  }, [linkedTopics, topics]);

  if (tags.length === 0 && all.length === 0) {
    return <span className="ated-hint">Pelo menos um. É por aqui que o artigo é encontrado.</span>;
  }
  const places = [
    ...tags.map(t => <strong key={`t-${t}`}>#{t}</strong>),
    ...all.map(t => (
      <strong key={`p-${t.topicId}`} title={`${t.courseName}${t.viaTag && !t.direct ? ` · via #${t.viaTag}` : ''}`}>
        {t.name} <span>(Fase {t.fase})</span>
      </strong>
    )),
  ];
  return (
    <span className="ated-hint ated-appear">
      Também vai aparecer em{' '}
      {places.map((el, i) => (
        <React.Fragment key={el.key}>
          {i > 0 && (i === places.length - 1 ? ' e ' : ', ')}
          {el}
        </React.Fragment>
      ))}
      .
    </span>
  );
}

/** Capa 16:9 compacta: miniatura + texto; arrastar e soltar ou escolher o arquivo. */
function CoverField({ coverUrl, onChange, onError }) {
  const [over, setOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      onChange(await articleService.uploadCover(file));
    } catch (err) {
      onError(apiErrorMessage(err, 'Não foi possível enviar a capa.'));
    } finally {
      setUploading(false);
    }
  };
  const fileInput = (
    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden
           onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
  );

  return (
    <div className="ated-cover">
      <label
        className={`ated-cover__thumb ${coverUrl ? 'has-image' : ''} ${over ? 'is-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); upload(e.dataTransfer.files?.[0]); }}
        aria-label={coverUrl ? 'Trocar capa' : 'Escolher capa'}
      >
        {coverUrl ? <img src={coverUrl} alt="" /> : <ImagePlus size={16} aria-hidden="true" />}
        {fileInput}
      </label>
      <div className="ated-cover__text">
        <strong>Capa</strong>
        {uploading ? <span>Enviando...</span> : coverUrl ? (
          <span className="ated-cover__actions">
            <label className="ated-textbtn">Trocar{fileInput}</label>
            <button type="button" className="ated-textbtn" onClick={() => onChange(null)}>Remover</button>
          </span>
        ) : <span>16:9 · até 5 MB · arraste ou clique</span>}
      </div>
    </div>
  );
}

/** Escolhe o curso e o tópico; opcionalmente a aula, as questões e os flashcards do tópico. */
function LinkModal({ courses, onClose, onAdd }) {
  const [courseSlug, setCourseSlug] = useState(courses[0]?.slug || '');
  const [topics, setTopics] = useState([]);
  const [topicId, setTopicId] = useState('');
  const [lessons, setLessons] = useState([]);
  const [lessonId, setLessonId] = useState('');
  const [questions, setQuestions] = useState(false);
  const [cards, setCards] = useState(false);

  useEffect(() => {
    setTopics([]);
    setTopicId('');
    if (!courseSlug) return;
    topicService.getAllTopics(false, courseSlug).then(setTopics).catch(() => setTopics([]));
  }, [courseSlug]);

  useEffect(() => {
    setLessons([]);
    setLessonId('');
    if (!topicId) return;
    lessonService.getLessonsByTopicId(topicId).then(setLessons).catch(() => setLessons([]));
  }, [topicId]);

  const course = courses.find(c => c.slug === courseSlug);
  const topic = topics.find(t => String(t.id) === String(topicId));

  const add = () => {
    const base = {
      topicId: topic.id, topicName: topic.name, fase: topic.fase || 1,
      courseName: course?.name, courseSlug, courseColor: course?.color,
    };
    const out = [{ ...base, kind: 'topic', targetId: topic.id, name: topic.name }];
    const lesson = lessons.find(l => String(l.id) === String(lessonId));
    if (lesson) out.push({ ...base, kind: 'lesson', targetId: lesson.id, name: lesson.name, lessonNumber: lesson.displayOrder });
    if (questions) out.push({ ...base, kind: 'question_set', targetId: topic.id });
    if (cards) out.push({ ...base, kind: 'flashcard_set', targetId: topic.id });
    onAdd(out);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Vincular conteúdo"
      width={520}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button icon={Link2} onClick={add} disabled={!topic}>Vincular</Button>
        </>
      )}
    >
      <Field label="Curso">
        <select value={courseSlug} onChange={(e) => setCourseSlug(e.target.value)}>
          {courses.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Tópico">
        <select value={topicId} onChange={(e) => setTopicId(e.target.value)}>
          <option value="">Escolha o tópico</option>
          {topics.map(t => <option key={t.id} value={t.id}>Fase {t.fase || 1} · {t.name}</option>)}
        </select>
      </Field>
      <Field label="Aula (opcional)">
        <select value={lessonId} onChange={(e) => setLessonId(e.target.value)} disabled={!topicId}>
          <option value="">Nenhuma aula específica</option>
          {lessons.map(l => <option key={l.id} value={l.id}>Aula {l.displayOrder} · {l.name}</option>)}
        </select>
      </Field>
      <label className="ated-check"><input type="checkbox" checked={questions} onChange={(e) => setQuestions(e.target.checked)} disabled={!topicId} />Questões do tópico (Pratique o tema)</label>
      <label className="ated-check"><input type="checkbox" checked={cards} onChange={(e) => setCards(e.target.checked)} disabled={!topicId} />Flashcards do tópico</label>
    </Modal>
  );
}

export default ArticleEditor;
